import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '../shared/Toast';

function BudgetModal({ budget, categories, onClose, onSave }) {
  const [form, setForm] = useState(
    budget
      ? { categoryId: budget.categoryId, planned: String(budget.planned), alertPct: budget.alertPct }
      : { categoryId: categories[0]?.id || '', planned: '', alertPct: 80 }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.categoryId) { toast.error('Selecione uma categoria.'); return; }
    if (!form.planned || parseFloat(form.planned) <= 0) { toast.error('Digite um valor válido.'); return; }
    onSave({ ...form, planned: parseFloat(form.planned), alertPct: parseInt(form.alertPct) || 80 });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          {budget ? 'Editar orçamento' : 'Novo orçamento'}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {!budget && (
          <div className="form-group">
            <label className="form-label">Categoria</label>
            <select className="form-control" value={form.categoryId} onChange={e => set('categoryId', e.target.value)} autoFocus>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Limite mensal (R$) *</label>
            <input
              className="form-control"
              type="number"
              step="10"
              min="1"
              placeholder="0,00"
              value={form.planned}
              onChange={e => set('planned', e.target.value)}
              autoFocus={!!budget}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Alertar em (%)</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="100"
              value={form.alertPct}
              onChange={e => set('alertPct', e.target.value)}
            />
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
          <i className="fas fa-bell" style={{ marginRight: 6, color: 'var(--gold)' }}></i>
          Você receberá um alerta quando atingir <strong>{form.alertPct}%</strong> do limite.
        </div>

        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Salvar orçamento
        </button>
      </div>
    </div>
  );
}

export default function Budgets() {
  const { state, dispatch } = useApp();
  const [modal, setModal] = useState(false);
  const [editBudget, setEditBudget] = useState(null);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const monthName = now.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

  const catMap = useMemo(
    () => Object.fromEntries(state.categories.map(c => [c.id, c])),
    [state.categories]
  );

  // Current month spending per category
  const spent = useMemo(() => {
    const map = {};
    state.transactions
      .filter(t => t.date.startsWith(currentMonth) && t.type === 'expense' && t.status === 'paid')
      .forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.value; });
    return map;
  }, [state.transactions, currentMonth]);

  // Categories not yet budgeted (for modal)
  const budgetedIds = new Set(state.budgets.map(b => b.categoryId));
  const availableCategories = state.categories.filter(c => c.type === 'expense' && !budgetedIds.has(c.id));

  const totalPlanned = state.budgets.reduce((s, b) => s + b.planned, 0);
  const totalSpent = state.budgets.reduce((s, b) => s + (spent[b.categoryId] || 0), 0);
  const totalAvailable = totalPlanned - totalSpent;

  const handleSave = (form) => {
    if (editBudget) {
      dispatch({ type: 'UPDATE_BUDGET', payload: { ...editBudget, ...form } });
      toast.success('Orçamento atualizado!');
    } else {
      dispatch({ type: 'ADD_BUDGET', payload: { id: uuidv4(), month: currentMonth, ...form } });
      toast.success('Orçamento criado!');
    }
  };

  const deleteBudget = (id) => {
    dispatch({ type: 'DELETE_BUDGET', payload: id });
    toast.info('Orçamento removido.');
  };

  return (
    <div className="page-enter">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Orçamento Poderoso</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Defina limites e controle seus gastos</div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setEditBudget(null); setModal(true); }}
          disabled={availableCategories.length === 0}
          title={availableCategories.length === 0 ? 'Todas as categorias já possuem orçamento' : ''}
        >
          <i className="fas fa-plus"></i> Nova Categoria
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Orçamento Total</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginTop: 6 }}>{formatCurrency(totalPlanned)}</div>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Gasto este mês</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: totalSpent > 0 ? '#f97316' : 'var(--text)', marginTop: 6 }}>{formatCurrency(totalSpent)}</div>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Disponível</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: totalAvailable >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 6 }}>{formatCurrency(totalAvailable)}</div>
        </div>
      </div>

      {/* Month label */}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, textTransform: 'capitalize' }}>
        <i className="fas fa-calendar" style={{ marginRight: 6, color: 'var(--gold)' }}></i>
        {monthName}
      </div>

      {/* Budget items */}
      {state.budgets.length === 0 ? (
        <div className="empty-state card">
          <i className="fas fa-clipboard-list"></i>
          <div>Nenhum orçamento configurado</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Crie orçamentos para controlar seus gastos por categoria</div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => { setEditBudget(null); setModal(true); }}>
            <i className="fas fa-plus"></i> Criar primeiro orçamento
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {state.budgets.map(budget => {
            const cat = catMap[budget.categoryId];
            const spentVal = spent[budget.categoryId] || 0;
            const pct = budget.planned > 0 ? Math.min(100, Math.round(spentVal / budget.planned * 100)) : 0;
            const isOver = spentVal > budget.planned;
            const isAlert = !isOver && pct >= budget.alertPct;
            const barColor = isOver ? '#e24b4a' : isAlert ? '#f97316' : '#e2e8f0';
            const fillColor = isOver ? '#e24b4a' : isAlert ? '#f97316' : 'var(--navy)';
            const spentColor = isOver ? '#e24b4a' : isAlert ? '#f97316' : 'var(--muted)';

            return (
              <div key={budget.id} className="card" style={{ marginBottom: 0, padding: '16px 20px' }}>
                {/* Row 1: dot + name + actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{
                    width: 11, height: 11, borderRadius: '50%',
                    background: cat?.color || '#888', flexShrink: 0,
                    boxShadow: `0 0 0 3px ${(cat?.color || '#888')}22`,
                  }}></span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', flex: 1 }}>
                    {cat?.name || budget.categoryId}
                  </span>
                  {isAlert && !isOver && (
                    <span className="tag tag-amber" style={{ fontSize: 10 }}>
                      <i className="fas fa-triangle-exclamation"></i> {pct}%
                    </span>
                  )}
                  {isOver && (
                    <span className="tag tag-red" style={{ fontSize: 10 }}>
                      <i className="fas fa-circle-exclamation"></i> Estourado
                    </span>
                  )}
                  <button
                    onClick={() => { setEditBudget(budget); setModal(true); }}
                    title="Editar"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    onClick={() => deleteBudget(budget.id)}
                    title="Remover"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, transition: 'background 0.12s, color 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--muted)'; }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Progress bar */}
                <div style={{ background: barColor, borderRadius: 99, height: 6, marginBottom: 8, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 99, background: fillColor, width: `${pct}%`, transition: 'width 0.6s ease' }}></div>
                </div>

                {/* Row 3: spent + limit */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: spentColor, fontWeight: 500 }}>
                    {formatCurrency(spentVal)} gastos
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>
                    limite: {formatCurrency(budget.planned)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tip card */}
      {state.budgets.length > 0 && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(201,162,39,0.07)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: 'var(--radius)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <i className="fas fa-lightbulb" style={{ color: 'var(--gold)', marginTop: 2, flexShrink: 0 }}></i>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
            <strong>Dica:</strong> Marque suas transações como "Pagas" para que os gastos apareçam no orçamento. Alertas são disparados automaticamente ao atingir o percentual configurado.
          </div>
        </div>
      )}

      {modal && (
        <BudgetModal
          budget={editBudget}
          categories={editBudget ? [catMap[editBudget.categoryId]].filter(Boolean) : availableCategories}
          onClose={() => { setModal(false); setEditBudget(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
